import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { withBuilder } from "@builder.io/react";

// Components
import Image from "../Atoms/Image";
import { ImgixImage } from "../Utils/ImgixImage";

// Stylesheets
import "./ImageGallery.scss";

// Constants
import { IMGIX_BASE_PATH } from "../../utils/constants";

export const ImageGalleryContext = React.createContext({
  selectedId: 0,
  setSelectedImage: () => {},
});

export const ImageGalleryProvider = ({ children }) => {
  // For our regular image gallery
  const [selectedId, setSelectedId] = useState(0);
  const setSelectedImage = (idx) => (e) => {
    return setSelectedId(idx);
  };

  return (
    <ImageGalleryContext.Provider
      value={{
        selectedId,
        setSelectedImage,
      }}
    >
      {children}
    </ImageGalleryContext.Provider>
  );
};

ImageGalleryProvider.propTypes = {
  children: PropTypes.any,
};

export const ImageGalleryThumbnails = ({
  thumbnailImages,
  shape,
  useImgix,
  isShopify,
  lazyLoading,
  startTabIndex,
  ...rest
}) => {
  const { selectedId, setSelectedImage } = useContext(ImageGalleryContext);
  const { src, alt, ...baseImageProps } = rest;
  const useTabIndex = startTabIndex >= 0;
  let lazyLoadProp = {};

  if (lazyLoading) {
    lazyLoadProp = { loading: "lazy" };

    if (useImgix) {
      lazyLoadProp = { htmlAttributes: lazyLoadProp };
    }
  }

  const imageProps = {
    ...baseImageProps,
    ...lazyLoadProp,
  };

  const renderThumbnails = () => {
    return thumbnailImages.map((image, idx) => {
      const className = classNames("thumbnail_wrapper", `is_${shape}`, {
        is_featured: idx === selectedId,
      });
      const tabIndex = useTabIndex ? startTabIndex + (idx + 1) : -1;

      let component = <Image src={image.src} alt={image.alt} {...imageProps} />;

      if (useImgix) {
        component = (
          <ImgixImage
            src={image.src}
            alt={image.alt}
            isShopify={isShopify}
            {...imageProps}
          />
        );
      }

      return (
        <button
          className={className}
          key={idx}
          data-testid="gallery_thumbnail_container"
          onClick={setSelectedImage(idx)}
          tabIndex={tabIndex}
        >
          {component}
        </button>
      );
    });
  };

  return (
    <div className="thumbnails" data-testid="gallery_thumbnails">
      {renderThumbnails()}
    </div>
  );
};

ImageGalleryThumbnails.propTypes = {
  thumbnailImages: PropTypes.array.isRequired,
  shape: PropTypes.oneOf(["square", "circle"]),
  useImgix: PropTypes.bool,
  isShopify: PropTypes.bool,
  lazyLoading: PropTypes.bool,
  startTabIndex: PropTypes.number,
};

ImageGalleryThumbnails.defaultProps = {
  shape: "square",
  useImgix: true,
  isShopify: false,
};

export const ImageGalleryFeaturedImage = ({
  images,
  className,
  useImgix,
  isShopify,
  lazyLoading,
  startTabIndex,
  displayImageCallout,
  ...rest
}) => {
  const imageClasses = classNames("featured_image", className);
  const { selectedId } = useContext(ImageGalleryContext);
  const featuredImage = images[selectedId];
  const loading = lazyLoading ? "lazy" : null;
  const imageProps = {
    ...rest,
    alt: featuredImage.alt,
    src: featuredImage.src,
    className: imageClasses,
    loading,
  };

  const htmlAttributes = {
    "data-testid": "gallery_feature",
    loading,
    tabIndex: startTabIndex,
  };

  if (useImgix) {
    return (
      <>
        {imageProps.src ? (
          <>
            {imageProps.src.includes(".gif") ? (
              <img src={imageProps.src} alt={imageProps.alt} />
            ) : (
              <ImgixImage
                htmlAttributes={htmlAttributes}
                isShopify={isShopify}
                {...imageProps}
              />
            )}
            {}
            {imageProps.src.includes("Carousel1withoutbar")
              ? displayImageCallout
              : null}
          </>
        ) : null}
      </>
    );
  }

  return <Image data-testid="gallery_feature" {...imageProps} />;
};

ImageGalleryFeaturedImage.propTypes = {
  images: PropTypes.array.isRequired,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  useImgix: PropTypes.bool,
  isShopify: PropTypes.bool,
  lazyLoading: PropTypes.bool,
  startTabIndex: PropTypes.number,
};

ImageGalleryFeaturedImage.defaultProps = {
  useImgix: true,
  isShopify: false,
};

export const Gallery = ({
  displayImageCallout,
  images,
  customThumbnailImages,
  featuredImageProps,
  thumbnailImageProps,
  className,
  useImgix,
  isShopify,
  lazyLoading,
  startTabIndex = -1,
}) => {
  const classes = classNames("image_gallery", className);

  return (
    <div className={classes}>
      <ImageGalleryFeaturedImage
        images={images}
        useImgix={useImgix}
        isShopify={isShopify}
        lazyLoading={lazyLoading}
        startTabIndex={startTabIndex}
        {...featuredImageProps}
        displayImageCallout={displayImageCallout}
      />

      <ImageGalleryThumbnails
        thumbnailImages={customThumbnailImages || images}
        useImgix={useImgix}
        isShopify={isShopify}
        lazyLoading={lazyLoading}
        startTabIndex={startTabIndex}
        {...thumbnailImageProps}
      />
    </div>
  );
};

Gallery.propTypes = {
  images: PropTypes.array.isRequired,
  displayImageCallout: PropTypes.any,
  featuredImageProps: PropTypes.object,
  thumbnailImageProps: PropTypes.object,
  customThumbnailImages: PropTypes.array,
  useImgix: PropTypes.bool,
  isShopify: PropTypes.bool,
  className: PropTypes.any,
  lazyLoading: PropTypes.bool,
  startTabIndex: PropTypes.number,
};

Gallery.defaultProps = {
  useImgix: true,
  isShopify: false,
};

export const ImageGallery = (props) => {
  return (
    <ImageGalleryProvider>
      <Gallery {...props} />
    </ImageGalleryProvider>
  );
};

// For Builder.io, wrap the component in an HOC that has image data from the
// Builder component inputs.
const withBuilderGalleryProps = (WrappedComponent) => {
  const WrappedGallery = ({ images: imageInputs }) => {
    const images = imageInputs.map((image) => ({
      src: image.image,
      alt: image["Alt Text"] || "",
    }));
    const componentProps = {
      images,
      useImgix: false,
      className: "builder_wrapped",
    };

    return <WrappedComponent {...componentProps} />;
  };

  WrappedGallery.propTypes = {
    images: PropTypes.array.isRequired,
  };

  return WrappedGallery;
};

const defaultImage = `${IMGIX_BASE_PATH}/rest-plus-gallery-4@2x.jpg`;

const defaultImages = Array.from({ length: 4 }, (image) => ({
  image: defaultImage,
}));

const BuilderImageGalleryComponent = withBuilderGalleryProps(ImageGallery);

export const ImageGalleryBlock = withBuilder(BuilderImageGalleryComponent, {
  name: "Image Gallery",
  image: "https://hatchbaby.imgix.net/webassets/gallery-icon-builder.svg",
  inputs: [
    {
      name: "images",
      type: "list",
      subFields: [
        {
          name: "image",
          type: "file",
          allowedFileTypes: ["jpg", "jpeg", "png"],
          required: true,
          defaultValue: {
            src: defaultImage,
            alt: "",
          },
        },
        {
          name: "Alt Text",
          type: "string",
          defaultValue: "",
        },
      ],
      defaultValue: defaultImages,
    },
  ],
});

const ImageGallerySet = {
  ImageGalleryThumbnails,
  ImageGalleryFeaturedImage,
  ImageGallery,
};

export default ImageGallerySet;
